// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useStoresList from "@/hooks/useStoresList";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useStore from "@/hooks/useStore";
import type { TyActiveStore } from "@/hooks/useStore/types";
import useTranslation from "@/hooks/useTranslation";
import type { TyStoreMembership } from "@/types";
import Skeleton from "@/UI/atoms/Skeleton";
import SettingsPreferences from "@/UI/components/SettingsPreferences";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import EditProfilePopup from "@/UI/popups/EditProfilePopup";
import LogoutPopup from "@/UI/popups/LogoutPopup";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type TyStoreItem = TyStoreMembership;

// --- Helpers ------------------------------------------------------------------------------------

const storeName = (store: TyActiveStore, lang: string | null) => store.name.find((n) => n.lang === lang)?.value ?? store.name[0]?.value ?? "—";

// --- Logic --------------------------------------------------------------------------------------

export default function ProfileScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { addComp, removeComp } = usePopup();

    const client = useAuth((state) => state.client);
    const activeStoreId = useStore((state) => state.storeId);
    const setStore = useStore((state) => state.setStore);

    const { stores, loading } = useStoresList();

    const activeStore = stores.find((store) => store._id === activeStoreId) ?? null;
    const otherStores = stores.filter((store) => store._id !== activeStoreId);
    const activeAccess = activeStore?.storeAccess ?? null;
    const isOwnerHere = activeStore?.isOwner ?? false;

    const modelLabels: Record<string, string> = {
        menu: t.sideBar.menu,
        branches: t.sideBar.branches,
        clients: t.sideBar.clients,
        subscriptions: t.sideBar.subscriptions,
        settings: t.sideBar.settings,
    };

    const actionLabels: Record<string, string> = { view: t.profile.actionView, edit: t.profile.actionEdit };

    const genderLabel =
        client?.gender === "male" ? t.profile.genderMale : client?.gender === "female" ? t.profile.genderFemale : t.profile.genderUnknown;

    // An owner is not described by a permission list, so the matrix is replaced by a single badge.
    const permissions = activeAccess?.permissions ?? [];
    const branches = activeAccess?.branches ?? "all";

    // Switching stores changes what every screen is looking at, so it is always confirmed first.
    // The client then stays put on the profile — the sections below re-render against the new store.
    const handleSwitchStore = (store: TyStoreItem) => {
        if (store._id === activeStoreId) return;
        removeComp("confirm-switch-store");
        addComp({
            name: "confirm-switch-store",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                message: `${t.profile.switchStoreConfirmMessage} ${storeName(store, lang)}`,
                icon: "/icons/store-icon.svg",
                variant: "primary",
                confirmLabel: t.profile.switchStoreConfirmAction,
                cancelLabel: t.profile.logoutCancel,
                onConfirm: () => setStore(store._id, store),
            },
        });
    };

    // Editing your own details is not a store setting, so it stays here rather than behind the
    // settings tab — which a client without `settings` access never sees.
    const openEditProfile = () => {
        removeComp("edit-profile");
        addComp({ name: "edit-profile", Component: EditProfilePopup, place: "center", anim: "scale", cover: "shaded", coverClose: true });
    };

    const openLogout = () => {
        removeComp("logout");
        addComp({ name: "logout", Component: LogoutPopup, place: "center", anim: "scale", cover: "shaded", coverClose: true });
    };

    return (
        <div className="profile-screen">
            <div className="profile-screen-content">
                {/* --- Identity --------------------------------------------------------------- */}
                <section className="profile-identity">
                    <div className="profile-identity-avatar">
                        {client?.picture ? (
                            <img src={client.picture} alt={client.name} />
                        ) : (
                            <img className="profile-identity-placeholder" src="/icons/client-small-placeholder-icon.svg" alt="" />
                        )}
                        <span className={`profile-identity-badge${isOwnerHere ? "" : " profile-identity-badge--staff"}`}>
                            {isOwnerHere ? t.profile.ownerBadge : t.profile.staffBadge}
                        </span>
                    </div>
                    <p className="profile-identity-name">{client?.name ?? "—"}</p>
                    <div className="profile-identity-chips">
                        <span className="profile-chip" dir="ltr">
                            <img src="/icons/lock-icon.svg" alt="" width={13} height={13} />
                            {client?.phone ?? "—"}
                        </span>
                        <span className="profile-chip">
                            <img src="/icons/gender-icon.svg" alt="" width={13} height={13} />
                            {genderLabel}
                        </span>
                    </div>
                    <button type="button" className="profile-identity-edit" onClick={openEditProfile}>
                        <img src="/icons/edit-icon.svg" alt="" width={13} height={13} />
                        <span>{t.profile.editProfileAction}</span>
                    </button>
                </section>

                {/* --- Stores ----------------------------------------------------------------- */}
                <section className="profile-block">
                    <div className="profile-block-head">
                        <p className="profile-block-title">{t.profile.storesTitle}</p>
                        <p className="profile-block-subtitle">{t.profile.storesSubtitle}</p>
                    </div>
                    {loading && <Skeleton className="profile-skeleton" />}
                    {!loading && (
                        <>
                            {activeStore && (
                                <div className="profile-store profile-store--current">
                                    <div className="profile-store-logo profile-store-logo--current">
                                        {activeStore.logo ? (
                                            <img src={activeStore.logo} alt={storeName(activeStore, lang)} />
                                        ) : (
                                            <span>{storeName(activeStore, lang).charAt(0)}</span>
                                        )}
                                    </div>
                                    <div className="profile-store-info">
                                        <p className="profile-store-name profile-store-name--current">{storeName(activeStore, lang)}</p>
                                        <p className="profile-store-meta">
                                            {activeStore.isOwner
                                                ? t.profile.ownerFullAccess
                                                : `${activeStore.storeAccess?.permissions.length ?? 0} ${t.profile.permissionsCount}`}
                                        </p>
                                    </div>
                                    <span className="profile-store-active">{t.profile.activeStoreBadge}</span>
                                </div>
                            )}

                            {otherStores.length > 0 && (
                                <>
                                    <p className="profile-stores-label">{t.profile.otherStores}</p>
                                    <div className="profile-stores">
                                        {otherStores.map((store) => {
                                            const name = storeName(store, lang);
                                            const count = store.isOwner ? null : (store.storeAccess?.permissions.length ?? 0);
                                            return (
                                                <div key={store._id} className="profile-store">
                                                    <div className="profile-store-logo">
                                                        {store.logo ? <img src={store.logo} alt={name} /> : <span>{name.charAt(0)}</span>}
                                                    </div>
                                                    <div className="profile-store-info">
                                                        <p className="profile-store-name">{name}</p>
                                                        <p className="profile-store-meta">
                                                            {count === null ? t.profile.ownerFullAccess : `${count} ${t.profile.permissionsCount}`}
                                                        </p>
                                                    </div>
                                                    {/* Only this button switches — the card itself is inert on purpose. */}
                                                    <button type="button" className="profile-store-switch" onClick={() => handleSwitchStore(store)}>
                                                        {t.profile.switchStoreAction}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </section>

                {/* --- Permissions in the active store ---------------------------------------- */}
                <section className="profile-block">
                    <div className="profile-block-head">
                        <p className="profile-block-title">{t.profile.permissionsTitle}</p>
                        <p className="profile-block-subtitle">{t.profile.permissionsSubtitle}</p>
                    </div>
                    {isOwnerHere ? (
                        <div className="profile-owner-note">
                            <img src="/icons/check-icon.svg" alt="" width={14} height={14} />
                            <span>{t.profile.ownerFullAccess}</span>
                        </div>
                    ) : permissions.length === 0 ? (
                        <p className="profile-empty">{t.profile.noPermissions}</p>
                    ) : (
                        <div className="profile-permissions">
                            {permissions.map((permission) => (
                                <div key={permission.model} className="profile-permission">
                                    <span className="profile-permission-model">{modelLabels[permission.model] ?? permission.model}</span>
                                    <div className="profile-permission-actions">
                                        {permission.actions.map((action) => (
                                            <span key={action} className="profile-action-chip">
                                                {actionLabels[action] ?? action}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* --- Branch scope ----------------------------------------------------------- */}
                <section className="profile-block">
                    <div className="profile-block-head">
                        <p className="profile-block-title">{t.profile.branchesTitle}</p>
                        <p className="profile-block-subtitle">{t.profile.branchesSubtitle}</p>
                    </div>
                    {isOwnerHere || branches === "all" ? (
                        <div className="profile-branches">
                            <span className="profile-branch-chip profile-branch-chip--all">{t.profile.allBranches}</span>
                        </div>
                    ) : branches.length === 0 ? (
                        <p className="profile-empty">{t.profile.noBranches}</p>
                    ) : (
                        <div className="profile-branches">
                            {branches.map((branch) => (
                                <span key={branch._id} className="profile-branch-chip">
                                    {branch.name}
                                </span>
                            ))}
                        </div>
                    )}
                </section>

                {/* --- Preferences: this browser only, so it belongs to the person, not the store --- */}
                <SettingsPreferences />

                {/* --- Logout: present, but the least loud thing on the page ------------------ */}
                <div className="profile-logout-row">
                    <button type="button" className="profile-logout" onClick={openLogout}>
                        {t.profile.logoutAction}
                    </button>
                </div>
            </div>
        </div>
    );
}
